import { router } from '@inertiajs/react';
import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { Button } from '@/Components/Button';
import { InputLabel } from '@/Components/InputLabel';
import { ModalView } from '@/Components/ModalView';
import TextInput, { TextInputHandle } from '@/Components/TextInput';
import { useToast } from '@/hook/useToast';
import { AppModalProps } from '@/types/modal.type';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { FruitBlueberrySvg } from '@/Components/Icons/FruitBlueberrySvg';
import { useRenameFlow } from '@/store/flows/flows.hook';

type FlowRenameModalProps = AppModalProps<{
    flowId: number;
    flowCode: string;
    flowName: string;
}>;

export const FlowRenameModal: FC<FlowRenameModalProps> = ({
    payload,
    onClose,
}) => {
    const flowId = payload?.flowId;
    const flowCode = payload?.flowCode;
    const flowName = payload?.flowName;

    const renameFlow = useRenameFlow();

    const [value, setValue] = useState<string>(flowName);
    const inputRef = useRef<TextInputHandle>(null);
    const { showToast, hideToast } = useToast();

    const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    }, []);

    const focusInput = () => {
        inputRef.current?.focus();
    };

    useEffect(() => {
        if (flowName && typeof flowName === 'string') {
            setValue(flowName || '');
        }
    }, [flowName]);

    const onSaveName = useCallback(() => {
        if (import.meta.env.DEV) {
            console.log('flowName', flowName);
        }

        onClose();

        return new Promise<null>((resolve, reject) => {
            hideToast();

            renameFlow(flowCode, value);

            router.patch(
                route('flows.updateName', { flowId }),
                { name: value },
                {
                    onSuccess: () => {
                        showToast({
                            variant: 'success',
                            label: 'Flow name updated successfully',
                        });

                        resolve(null);
                    },
                    onError: (errors) => {
                        renameFlow(flowCode, flowName);

                        Object.keys(errors).forEach((errorKey) => {
                            showToast({
                                variant: 'danger',
                                label: errors[errorKey],
                                autoClose: false,
                            });
                        });

                        reject(errors);
                    },
                },
            );
        });
    }, [flowId, value, onClose]);

    return (
        <ModalView
            onAnimationStart={focusInput}
            maxWidth="sm"
            onSubmit={onSaveName}
            onClose={onClose}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 top-0 left-14" />

                <div className="absolute bottom-3.5 right-20">
                    <FruitBlueberrySvg className="" />
                </div>
            </div>
            <InputLabel
                htmlFor="flow-name"
                value="Name this Flow"
                className="w-full text-left mb-2 mt-2 !font-bold text-heading-five text-brand-ocean"
            />

            <TextInput
                id="flow-name"
                ref={inputRef}
                placeholder="Flow Name"
                className="w-full mb-2"
                value={value}
                onChange={onChange}
            />
            <div className="mt-2 w-full flex flex-row items-center justify-center space-x-3">
                <Button
                    className="flex-1 h-9"
                    variant="default"
                    onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    className="flex-1 h-9"
                    variant="primary"
                    onClick={onSaveName}>
                    Save
                </Button>
            </div>
        </ModalView>
    );
};
